if (!customElements.get('thb-product-v2')) {
  customElements.define('thb-product-v2', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.addEventListener('click', event => this.handleClick(event), options);
      this.addEventListener('change', event => this.handleQuantity(event), options);
      this.addEventListener('submit', event => this.guardSubmit(event), {...options, capture: true});
      this.addEventListener('shopify:cart:lines-update', event => this.handleCart(event), options);
      this.addEventListener('shopify:cart:error', event => this.handleCartError(event), options);
      window.addEventListener('popstate', () => this.loadVariant(new URL(location.href)), options);
      const dialog = this.querySelector('[data-pdp-dialog]');
      dialog?.addEventListener('close', () => {
        window.THBModalScroll?.unlock(dialog);
        this.zoomTrigger?.focus({preventScroll: true});
      }, options);
      dialog?.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      }, options);
      this.enhanceGallery();
      this.enhanceContentRails();
      this.updateQuantityButtons();
      this.loadRecommendations();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.variantRequest?.abort();
      if (this.variantPending) {
        const button = this.querySelector('[data-pdp-add]');
        if (button) button.disabled = this.variantRestoreDisabled;
        this.variantPending = false;
        this.removeAttribute('aria-busy');
      }
      this.deferredVariantUrl = null;
      this.recommendationRequest?.abort();
      this.galleryController?.abort();
      this.contentRailController?.abort();
      const dialog = this.querySelector('[data-pdp-dialog]');
      if (dialog?.open) dialog.close();
      window.THBModalScroll?.unlock(dialog);
    }
    handleClick(event) {
      const target = event.target.closest('a,button');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-pdp-option]')) {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(target.href);
        if (url.origin !== location.origin || url.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) return;
        event.preventDefault();
        if (this.cartPending) return;
        this.loadVariant(url, target.dataset.pdpOption, true);
      } else if (target.matches('[data-pdp-step]')) {
        const input = target.closest('[data-pdp-quantity]').querySelector('input');
        if (Number(target.dataset.pdpStep) > 0) input.stepUp(); else input.stepDown();
        input.dispatchEvent(new Event('change', {bubbles: true}));
      } else if (target.matches('[data-pdp-zoom]')) {
        const dialog = this.querySelector('[data-pdp-dialog]');
        if (!dialog?.showModal) return;
        event.preventDefault();
        this.zoomTrigger = target;
        const image = dialog.querySelector('[data-pdp-zoom-image]');
        image.src = target.href;
        image.alt = target.querySelector('img')?.alt || '';
        window.THBModalScroll?.lock(dialog);
        try {dialog.showModal();} catch {window.THBModalScroll?.unlock(dialog);}
      } else if (target.matches('[data-pdp-close]')) this.querySelector('[data-pdp-dialog]')?.close();
    }
    handleQuantity(event) {
      if (!event.target.matches('[data-pdp-quantity] input')) return;
      const input = event.target;
      input.closest('product-form-component').dataset.quantityDefault = input.value;
      this.updateQuantityButtons();
    }
    updateQuantityButtons() {
      const quantity = this.querySelector('[data-pdp-quantity]');
      if (!quantity) return;
      const input = quantity.querySelector('input');
      quantity.querySelector('[data-pdp-step="-1"]').disabled = this.cartPending || Number(input.value) <= Number(input.min || 1);
      quantity.querySelector('[data-pdp-step="1"]').disabled = this.cartPending || Boolean(input.max && Number(input.value) >= Number(input.max));
    }
    guardSubmit(event) {
      if (!event.target.matches('.thb-pdp__form')) return;
      if (this.cartPending || this.variantPending || !event.target.reportValidity() || !event.target.querySelector('input[name="id"]:not(:disabled)') || event.target.querySelector('[data-pdp-add]').disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
    handleCart(event) {
      const main = this.querySelector('[data-pdp-main]');
      if (event.action !== 'add' || !main?.contains(event.target) || !event.promise) return;
      this.cartPending = true;
      const form = event.target;
      form.setAttribute('aria-busy', 'true');
      const button = form.querySelector('[data-pdp-add]');
      if (button) button.disabled = true;
      this.updateQuantityButtons();
      this.showNotice('');
      event.promise.then(({detail}) => {
        if (detail?.didError) return;
        this.showNotice(this.dataset.cartAdded);
        if (!document.querySelector('theme-drawer#cart-drawer')) location.assign(this.dataset.cartUrl);
      }).catch(() => this.showNotice(this.dataset.cartError, true)).finally(() => {
        this.cartPending = false;
        form.removeAttribute('aria-busy');
        if (button && button.isConnected) button.disabled = false;
        this.updateQuantityButtons();
        const deferredUrl = this.deferredVariantUrl;
        this.deferredVariantUrl = null;
        if (deferredUrl && this.isConnected) this.loadVariant(deferredUrl);
      });
    }
    handleCartError(event) {
      if (!this.querySelector('[data-pdp-main]')?.contains(event.target)) return;
      const message = event.code === 'SERVICE_UNAVAILABLE' ? this.dataset.cartError : event.detail?.description || event.error || this.dataset.cartError;
      this.showNotice(typeof message === 'string' ? message : this.dataset.cartError, true);
    }
    showNotice(message, focus = false) {
      const notice = this.querySelector('[data-pdp-notice]');
      notice.textContent = message || '';
      notice.hidden = !message;
      if (message && focus) notice.focus({preventScroll: false});
    }
    async loadVariant(url, focusId, updateHistory = false) {
      if (this.cartPending) {
        if (!updateHistory) this.deferredVariantUrl = new URL(url);
        return;
      }
      this.variantRequest?.abort();
      this.variantRequest = new AbortController();
      const signal = this.variantRequest.signal;
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      if (!this.variantPending) this.variantRestoreDisabled = this.querySelector('[data-pdp-add]')?.disabled;
      this.variantPending = true;
      this.setAttribute('aria-busy', 'true');
      const oldButton = this.querySelector('[data-pdp-add]');
      const previouslyDisabled = this.variantRestoreDisabled;
      if (oldButton) oldButton.disabled = true;
      try {
        const response = await fetch(requestUrl, {signal, credentials: 'same-origin', headers: {Accept: 'text/html'}});
        if (!response.ok) throw new Error('Product unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = [...html.querySelectorAll('thb-product-v2')].find(node => node.dataset.sectionId === this.dataset.sectionId);
        if (!incoming || incoming.dataset.productId !== this.dataset.productId || !incoming.querySelector('[data-pdp-main]')) throw new Error('Unexpected product response');
        if (signal.aborted || !this.isConnected) return;
        const quantity = Number(this.querySelector('[name="quantity"]')?.value || 1);
        this.querySelector('[data-pdp-main]').replaceWith(incoming.querySelector('[data-pdp-main]'));
        const input = this.querySelector('[name="quantity"]');
        const min = Number(input.min || 1), step = Number(input.step || 1), max = Number(input.max || Infinity);
        const alignedMax = Number.isFinite(max) ? min + Math.max(0, Math.floor((max - min) / step)) * step : Infinity;
        input.value = String(Math.min(alignedMax, min + Math.max(0, Math.floor((quantity - min) / step)) * step));
        input.dispatchEvent(new Event('change', {bubbles: true}));
        if (updateHistory) history.pushState({}, '', url);
        this.showNotice(Number(input.value) === quantity ? '' : this.dataset.quantityAdjusted.replace('__QUANTITY__', input.value));
        this.enhanceGallery();
        if (focusId) this.querySelector('[data-pdp-option="' + CSS.escape(focusId) + '"]')?.focus({preventScroll: true});
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (oldButton?.isConnected) oldButton.disabled = previouslyDisabled;
        this.showNotice(this.dataset.loadError, true);
        const fallback = document.createElement('a');
        fallback.href = url;
        fallback.textContent = this.dataset.retryLabel;
        this.querySelector('[data-pdp-notice]').append(' ', fallback);
      } finally {
        if (!signal.aborted) {this.variantPending = false; this.removeAttribute('aria-busy');}
      }
    }
    enhanceGallery() {
      const gallery = this.querySelector('[data-pdp-gallery]');
      if (!gallery) return;
      this.galleryController?.abort();
      this.galleryController = new AbortController();
      const gallerySignal = this.galleryController.signal;
      const links = [...gallery.querySelectorAll('[data-pdp-zoom]')];
      const frames = [...gallery.children];
      gallery.parentElement.querySelector('.thb-pdp__gallery-pages')?.remove();
      const pages = document.createElement('div');
      pages.className = 'thb-pdp__gallery-pages';
      pages.setAttribute('role', 'group');
      pages.setAttribute('aria-label', gallery.getAttribute('aria-label'));
      if (frames.length > 1) {
        frames.forEach((frame, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', `${gallery.getAttribute('aria-label')} ${index + 1} / ${frames.length}`);
          button.setAttribute('aria-controls', gallery.id);
          const marker = document.createElement('span');
          marker.setAttribute('aria-hidden', 'true');
          button.append(marker);
          button.addEventListener('click', () => {
            const left = gallery.scrollLeft + frame.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
            gallery.scrollTo({left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
          }, {signal: gallerySignal});
          pages.append(button);
        });
        gallery.after(pages);
      }
      const update = () => {
        const rect = gallery.getBoundingClientRect();
        const isRail = getComputedStyle(gallery).display === 'flex';
        gallery.tabIndex = isRail && gallery.scrollWidth > gallery.clientWidth ? 0 : -1;
        let closestIndex = 0, closestDistance = Infinity;
        frames.forEach((frame, index) => {
          const bounds = frame.getBoundingClientRect();
          const distance = Math.abs(bounds.left - rect.left);
          if (distance < closestDistance) {closestDistance = distance; closestIndex = index;}
          frame.inert = isRail && (bounds.left < rect.left - 2 || bounds.right > rect.right + 2);
          if (frame.inert) frame.querySelectorAll('video').forEach(video => video.pause());
        });
        [...pages.children].forEach((button, index) => button.setAttribute('aria-current', String(index === closestIndex)));
        links.forEach(link => {
          const frame = link.getBoundingClientRect();
          link.tabIndex = !isRail || (frame.left >= rect.left - 2 && frame.right <= rect.right + 2) ? 0 : -1;
        });
      };
      gallery.addEventListener('scroll', update, {passive: true, signal: gallerySignal});
      window.addEventListener('resize', update, {passive: true, signal: gallerySignal});
      gallery.addEventListener('keydown', event => {
        if (event.target !== gallery || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const left = event.key === 'Home' ? 0 : event.key === 'End' ? gallery.scrollWidth : gallery.scrollLeft + (event.key === 'ArrowLeft' ? -1 : 1) * (gallery.firstElementChild.getBoundingClientRect().width + 12);
        gallery.scrollTo({left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }, {signal: gallerySignal});
      requestAnimationFrame(update);
    }
    enhanceContentRails() {
      this.contentRailController?.abort();
      this.contentRailController = new AbortController();
      const signal = this.contentRailController.signal;
      this.querySelectorAll('.thb-pdp__detail-cards,.thb-pdp__category-grid,.thb-pdp__related-grid').forEach(rail => {
        const label = rail.closest('section')?.querySelector('h2')?.textContent || this.querySelector('.thb-pdp__accordions summary')?.textContent;
        rail.setAttribute('role', 'group');
        if (label) rail.setAttribute('aria-label', label.trim());
        const update = () => {
          const scrolling = getComputedStyle(rail).display === 'flex' && rail.scrollWidth > rail.clientWidth + 1;
          rail.tabIndex = scrolling ? 0 : -1;
          const rect = rail.getBoundingClientRect();
          [...rail.children].forEach(card => {
            const bounds = card.getBoundingClientRect();
            card.inert = scrolling && (bounds.left < rect.left - 2 || bounds.right > rect.right + 2);
          });
        };
        rail.addEventListener('scroll', update, {passive: true, signal});
        window.addEventListener('resize', update, {passive: true, signal});
        rail.addEventListener('keydown', event => {
          if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const step = (rail.firstElementChild?.getBoundingClientRect().width || rail.clientWidth) + parseFloat(getComputedStyle(rail).columnGap || 0);
          const left = event.key === 'Home' ? 0 : event.key === 'End' ? rail.scrollWidth : rail.scrollLeft + (event.key === 'ArrowLeft' ? -step : step);
          rail.scrollTo({left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }, {signal});
        requestAnimationFrame(update);
      });
    }
    async loadRecommendations() {
      const region = this.querySelector('[data-pdp-recommendations]');
      if (!region?.dataset.url) return;
      this.recommendationRequest = new AbortController();
      try {
        const response = await fetch(region.dataset.url, {signal: this.recommendationRequest.signal, credentials: 'same-origin'});
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-pdp-recommendations]');
        if (content?.querySelector('.thb-commerce-card') && this.isConnected) {
          region.innerHTML = content.innerHTML;
          this.enhanceContentRails();
        }
      } catch {}
    }
  });
}
